import axios from 'axios'
import type { AxiosError, AxiosInstance } from 'axios'
import router from '@/router'
import { authService, isMobileBridge } from './authService'
import { useToast } from 'vue-toastification'
import * as Sentry from '@sentry/vue'
import { useErrorStore } from '@/stores/errorStore'
import type { ApiErrorResponse } from '@/types/Api'

const isApiErrorResponse = (data: unknown): data is ApiErrorResponse => {
	if (typeof data !== 'object' || data === null) return false
	const body = data as Record<string, unknown>
	return Number.isInteger(body.statusCode) && typeof body.message === 'string' &&
		(body.title === undefined || typeof body.title === 'string')
}

const apiBaseURL =
	import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'

const api: AxiosInstance = axios.create({
	baseURL: apiBaseURL,
	timeout: 10000
})

// Request interceptor
api.interceptors.request.use(
	async (config) => {
		try {
			// apply auth token
			const token = await authService.getAccessToken()

			if (token && !config.skipAuth) {
				config.headers['Authorization'] = `Bearer ${token}`
			}

			if (!config.skipDefaultHeaders) {
				config.headers['Content-Type'] = 'application/json'
				config.headers['Accept-Language'] = 'cs-CZ'
			}

			return config
		} catch (error) {
			return Promise.reject(error)
		}
	},
	(error) => {
		return Promise.reject(error)
	}
)

// Response interceptor
api.interceptors.response.use(
	(response) => {
		return response
	},
	(error: AxiosError) => {
		if (error.response) {
			const body = error.response.data
			error.apiError = isApiErrorResponse(body)
				? { statusCode: body.statusCode, message: body.message, title: body.title }
				: { statusCode: error.response.status, message: 'Došlo k neočekávané chybě.' }
		}
		// global error handling
		if (error.response?.status === 401) {
			// unauthorized
			if (isMobileBridge()) {
				useErrorStore().setError({
					message: error.apiError?.message ?? 'Došlo k chybě při požadavku.',
					statusCode: error.apiError?.statusCode ?? error.response.status,
					title: error.apiError?.title,
					request: { method: error.config?.method, url: error.config?.url }
				})
				void router.push('/error')
			} else {
				authService.oidcInstance?.signOut()
			}
		} else {
			// Show toast for other errors
			const toast = useToast()
			let message = 'Došlo k nespecifikované chybě'
			message = error.apiError?.message ?? message
			toast.error(message)
			console.error('API response error', error)
		}

		// allow for individual error handling
		return Promise.reject(error)
	}
)

// Add lightweight Sentry breadcrumbs and capture for unhandled API errors
// This keeps UI toast behavior but ensures errors are sent to Sentry for investigation
api.interceptors.request.use((config) => {
	try {
		if ((Sentry as any)?.addBreadcrumb) {
			Sentry.addBreadcrumb({
				category: 'http',
				message: `${(config.method || '').toUpperCase()} ${config.url}`,
				level: 'info',
				data: {
					params: config.params
				}
			})
		}
	} catch (e) {
		// ignore breadcrumb errors
	}
	return config
})

api.interceptors.response.use(
	(response) => response,
	(error) => {
		try {
			if ((Sentry as any)?.captureException) {
				Sentry.captureException(error, {
					extra: {
						url: error?.config?.url,
						method: error?.config?.method,
						status: error?.response?.status,
						responseData: error?.response?.data
					}
				})
			}
		} catch (e) {
			// ignore capture errors
		}
		return Promise.reject(error)
	}
)

export default api
